import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose } from 'ramda';
import { Layout } from 'layouts';
import { HugggLife, Reaction } from 'components';
import { PageTitle } from 'components/misc';
import { PoweredByHuggg as UIPoweredByHuggg } from 'components/ui';
import { withHuggg, withRedirect } from 'hoc';
import { useAnalytics, usePersistedState } from 'hooks';
import { useThemeContext, Divider } from '@huggg/react-components';
import styles from './redeemed.module.scss';
import { isInIframe } from 'utilities/iframes';
import { RedeemMethodVoucherContainer } from 'components/Redeem';
import {
  getShowHugggLife,
  getShowReaction,
} from 'components/Redeem/Method/Voucher/utils';
import { getBasePathname } from 'utilities/huggg';

export const ScreensRedeemed = (props) => {
  /**
   * Threshold at which a user us classes as "experienced" so then we toggle
   * interface elements on for them.
   *
   * @var {Integer} interfaceThreshold
   */
  const interfaceThreshold = 2;
  const {
    huggg,
    isBlueHuggg,
    isFirstRedeemed,
    isRedeemed,
    hasVoucherToClaim,
    vouchers,
    hasFulfilledVoucher,
    isVirginExperience,
  } = props;

  const analytics = useAnalytics();
  const { shouldShowPoweredByHuggg, name: theme } = useThemeContext();
  const [viewedShortcodes, setViewedShortcodes] = usePersistedState(
    'viewedShortcodes',
    [],
  );

  const hasVouchers = useMemo(
    () => Boolean(vouchers && vouchers.length),
    [vouchers],
  );

  const hasReaction = useMemo(() => huggg?.hasReaction, [huggg.hasReaction]);

  const shouldShowHugggLife = useMemo(
    () =>
      getShowHugggLife({
        theme,
        isBlueHuggg,
        isRedeemed,
        hasVouchers,
        isInIframe,
      }),
    [theme, isBlueHuggg, isRedeemed, hasVouchers],
  );

  const shouldShowReaction = useMemo(
    () =>
      getShowReaction({
        hasReaction,
        isFirstRedemption: isFirstRedeemed,
        hasVoucherToClaim,
        isRedeemed,
        hasVouchers,
        isVirginExperience,
        hasFulfilledVoucher,
      }),
    [
      hasReaction,
      isRedeemed,
      isFirstRedeemed,
      hasVouchers,
      hasVoucherToClaim,
      isVirginExperience,
      hasFulfilledVoucher,
    ],
  );

  useEffect(() => {
    const shortcode = huggg.shortcode;
    const shouldStore =
      !viewedShortcodes.includes(shortcode) &&
      viewedShortcodes.length <= interfaceThreshold;

    isFirstRedeemed
      ? analytics.screenViewedHugggRedeemed()
      : analytics.screenViewedHugggRedeemedExpired();

    if (shouldStore) {
      setViewedShortcodes([...viewedShortcodes, shortcode]);
    }
  }, [
    analytics,
    huggg.shortcode,
    isFirstRedeemed,
    setViewedShortcodes,
    viewedShortcodes,
  ]);

  return (
    <Layout showBackButton={false}>
      <PageTitle huggg={huggg} />

      <div
        className={classnames('container', styles.container)}
        data-test-id="redeemed-wrapper"
      >
        <RedeemMethodVoucherContainer
          data-test-id="redeem-method-voucher"
          isRedeemed={true}
        />

        {shouldShowPoweredByHuggg && <UIPoweredByHuggg />}
      </div>

      <Divider />

      {shouldShowReaction && (
        <div className={styles.reaction}>
          <Reaction includeCommentSection={true} isDialog />
        </div>
      )}

      {shouldShowHugggLife && (
        <div className={styles.hugggLifeContainer}>
          <HugggLife />
        </div>
      )}
    </Layout>
  );
};

ScreensRedeemed.propTypes = {
  huggg: PropTypes.object.isRequired,
  isBlueHuggg: PropTypes.bool.isRequired,
  isFirstRedeemed: PropTypes.bool.isRequired,
  isRedeemed: PropTypes.bool.isRequired,
  isTestHuggg: PropTypes.bool.isRequired,
  isMultiRedeemable: PropTypes.bool.isRequired,
  sender: PropTypes.object.isRequired,
  hasVoucherToClaim: PropTypes.bool.isRequired,
  vouchers: PropTypes.array.isRequired,
  hasFulfilledVoucher: PropTypes.bool.isRequired,
  isVirginExperience: PropTypes.bool.isRequired,
};

const redirectToRedeemHandler = (props) => {
  return [
    !props.isRedeemed && !props.isFetchedHuggg && !props.hasFulfilledVoucher,
    `${getBasePathname(props.huggg)}/redeem`,
  ];
};

const augmentedWithHuggg = (screen) =>
  withHuggg(screen, { forceRefresh: { huggg: true } });

export default compose(
  augmentedWithHuggg,
  withRedirect(redirectToRedeemHandler),
)(ScreensRedeemed);
